<template>
  <div class="col-span-10 datav-border rounded-lg p-4 datav-gradient" style="height: 100%; display: flex; flex-direction: column;">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-lg font-semibold flex items-center gap-2">
        <i class="fa fa-bolt text-primary"></i>雪峰山发射基地发射机监测
      </h2>
      <div class="text-sm text-gray-300">
        <i class="fa fa-refresh mr-1"></i>最后更新: {{ lastUpdate }}
      </div>
    </div>
    <!-- Three.js 3D容器 -->
    <div ref="container" class="relative bg-dark-light rounded-lg p-3 h-[calc(100%-40px)] overflow-hidden" style="box-sizing: border-box;"></div>
    <!-- 设备详情面板 -->
    <div v-if="selectedDevice" class="device-details absolute bottom-4 left-4 bg-dark/80 text-white p-3 rounded-lg border border-primary/30 w-64 z-10">
      <h4 class="font-bold mb-2">{{ selectedDevice.name }}</h4>
      <div class="grid grid-cols-2 gap-2 text-sm text-left">
        <div><span class="text-gray-400 pl-2">功率:</span> {{ selectedDevice.power }}</div>
        <div><span class="text-gray-400 pl-2">反射:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">激励:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">电流A:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">电流B:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">电流C:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">电流D:</span> {{ selectedDevice.reflection }}</div>
        <div><span class="text-gray-400 pl-2">状态:</span> <span :class="selectedDevice.status === 'normal' ? 'text-green-400' : 'text-red-400'">{{ selectedDevice.status=== 'normal' ? '正常' : '告警' }}</span></div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import TWEEN from '@tweenjs/tween.js';

export default {
  name: 'DeviceLayoutTransmitter',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      labelRenderer: null,
      controls: null,
      racks: [],
      sensors: [],
      selectedDevice: null,
      raycaster: new THREE.Raycaster(),
      mouse: new THREE.Vector2(),
      animationId: null,
      lastUpdate: '',
      sensorData: {}
    };
  },
  mounted() {
    this.updateLastUpdateTime();
    this.$nextTick(() => {
      this.initThreeJS();
      this.create3DLayout();
      this.addRaycasterListener();
      this.animate();
      this.updateLastUpdateTime();
      window.addEventListener('resize', this.handleResize);
      this.startSensorDataUpdate();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    this.controls.dispose();
    this.renderer.dispose();
    cancelAnimationFrame(this.animationId);
  },
  methods: {
    initThreeJS() {
      const container = this.$refs.container;
      // 创建场景
      this.scene = new THREE.Scene();
      const gradientBackground = new THREE.TextureLoader().load('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjMWEyYTJlIi8+CjxyZWN0IHdpZHRoPSI1IiBoZWlnaHQ9IjUiIGZpbGw9IiMyOTM5N2UiIG9wYWNpdHk9IjAuOCIvPgo8L3N2Zz4=');
      gradientBackground.wrapS = THREE.RepeatWrapping;
      gradientBackground.wrapT = THREE.RepeatWrapping;
      gradientBackground.repeat.set(10, 10);
      this.scene.background = gradientBackground;

      // 创建相机
      this.camera = new THREE.PerspectiveCamera(55, container.clientWidth / container.clientHeight, 0.1, 1000);
      this.camera.position.set(0, 20, 25);
      this.camera.lookAt(0, 5, 0);

      // 创建渲染器
      this.renderer = new THREE.WebGLRenderer({ antialias: true });
      const style = getComputedStyle(container);
      const paddingX = parseInt(style.paddingLeft) + parseInt(style.paddingRight);
      const paddingY = parseInt(style.paddingTop) + parseInt(style.paddingBottom);
      this.renderer.setSize(container.clientWidth - paddingX, container.clientHeight - paddingY);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      container.appendChild(this.renderer.domElement);

      // 添加环境光
      const rackAmbientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.scene.add(rackAmbientLight);

      // 添加方向光
      const rackDirectionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      rackDirectionalLight.position.set(10, 20, 15);
      this.scene.add(rackDirectionalLight);

      // 创建CSS2D渲染器
      this.labelRenderer = new CSS2DRenderer();
      this.labelRenderer.setSize(container.clientWidth - paddingX, container.clientHeight - paddingY);
      this.labelRenderer.domElement.style.position = 'absolute';
      this.labelRenderer.domElement.style.top = '0';
      this.labelRenderer.domElement.style.pointerEvents = 'none';
      container.appendChild(this.labelRenderer.domElement);

      // 添加控制器
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.05;
      this.controls.maxPolarAngle = Math.PI / 2;

      // 添加灯光
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
      this.scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 20, 15);
      this.scene.add(directionalLight);

      // 添加网格地面
      const gridHelper = new THREE.GridHelper(50, 20, 0x3a3a5e, 0x2a2a4e);
      gridHelper.material.opacity = 0.5;
      gridHelper.material.transparent = true;
      this.scene.add(gridHelper);

      this.$nextTick(() => {
        setTimeout(() => {
          this.handleResize();
          this.renderer.render(this.scene, this.camera);
          this.labelRenderer.render(this.scene, this.camera);
        }, 100);
      });
    },
    create3DLayout() {
      // 创建地面
      const groundGeometry = new THREE.PlaneGeometry(40, 30);
      const groundMaterial = new THREE.MeshStandardMaterial({ color: 0xe9ecef });
      const ground = new THREE.Mesh(groundGeometry, groundMaterial);
      ground.rotation.x = -Math.PI / 2;
      this.scene.add(ground);

      // 创建设备布局 - 只显示customDeviceNumbers中的11台设备
      const customDeviceNumbers = [
        'FM93.2', 'FM101.6', 'FM95.2', 'FM91.8','FM91.2', 'FM107.6',
        'CH19', 'CH20', 'CH31', 'CH30', 'CH36'
      ];

      customDeviceNumbers.forEach((deviceNumber, index) => {
        const row = Math.floor(index / 4);
        const col = index % 4;
        const x = -15 + col * 10;
        const z = 10 - row * 10;

        // 创建机架（使用特色颜色）
        const rack = this.createRack(x, z, 0xff9800, deviceNumber);
        // 添加温度湿度传感器
        const power = (5 + Math.random() * 2).toFixed(1) + 'KW';
        const reflection = Math.floor(2 + Math.random() * 10) + 'W';
        const status =  reflection > 5 ? 'abnormal' : 'normal';
        this.sensors.push({
          id: deviceNumber,
          name: deviceNumber,
          power,
          reflection,
          status,
          rack
        });
        // 添加传感器标签调用
        // 初始化传感器数据
      this.sensorData[deviceNumber] = { power: parseFloat(power), reflection: parseFloat(reflection) };
      this.createSensor(x, z, 7, status === 'normal' ? 0x4caf50 : 0xf44336, deviceNumber);
      });
    },
    createRack(x, z, color, name) {
      // 创建机架组
      const rackGroup = new THREE.Group();
      rackGroup.position.set(x, 2, z);
      rackGroup.rotation.y = Math.PI;
      rackGroup.userData = { type: 'device', id: name };
      this.scene.add(rackGroup);
      this.racks.push(rackGroup);

      // 主机身
      const bodyGeometry = new THREE.BoxGeometry(2.5, 5.5, 2.25);
      const bodyMaterial = new THREE.MeshStandardMaterial({
        color: 0x2d2d44,
        metalness: 0.8,
        roughness: 0.3
      });
      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      rackGroup.add(body);

      // 前面板
      const panelGeometry = new THREE.BoxGeometry(2.3, 5.3, 0.1);
      const panelMaterial = new THREE.MeshStandardMaterial({ color: 0x1a1a2e });
      const panel = new THREE.Mesh(panelGeometry, panelMaterial);
      panel.position.z = -1.075;
      rackGroup.add(panel);

      // 显示屏
      const screenGeometry = new THREE.BoxGeometry(1.8, 1.2, 0.05);
      const screenMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        emissive: 0xcccccc,
        emissiveIntensity: 50
      });
      const screen = new THREE.Mesh(screenGeometry, screenMaterial);
      const wireframeGeometry = new THREE.WireframeGeometry(screenGeometry);
      const wireframeMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 });
      const wireframe = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);
      screen.add(wireframe);
      screen.position.set(0, 1.5, -1.07);
      rackGroup.add(screen);

      // 显示屏边框
      const screenBorderGeometry = new THREE.BoxGeometry(1.9, 1.3, 0.1);
      const screenBorderMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
      const screenBorder = new THREE.Mesh(screenBorderGeometry, screenBorderMaterial);
      screenBorder.position.set(0, 1.5, -1.075);
      rackGroup.add(screenBorder);

      // 按钮
      const buttonGeometry = new THREE.CircleGeometry(0.1, 16);
      const buttonMaterial = new THREE.MeshStandardMaterial({ color: 0x444444 });
      const buttonPositions = [
        [-0.6, -0.3], [0, -0.3], [0.6, -0.3],
        [-0.6, -0.6], [0, -0.6], [0.6, -0.6]
      ];
      buttonPositions.forEach(([bx, by]) => {
        const button = new THREE.Mesh(buttonGeometry, buttonMaterial);
        button.rotation.x = -Math.PI / 2;
        button.position.set(bx, by, -1.08);
        rackGroup.add(button);
      });

      // 通风口
      const ventGeometry = new THREE.BoxGeometry(2, 0.5, 0.05);
      const ventMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
      const vent = new THREE.Mesh(ventGeometry, ventMaterial);
      vent.position.set(0, -2, -1.08);
      rackGroup.add(vent);

      // 添加机架标签
      const div = document.createElement('div');
      div.className = 'rack-label';
      div.style.color = '#ffffff';
      div.style.backgroundColor = 'rgba(10, 10, 30, 0.7)';
      div.style.padding = '3px 8px';
      div.style.borderRadius = '4px';
      div.style.fontSize = '14px';
      div.style.fontWeight = 'bold';
      div.style.textAlign = 'center';
      div.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      div.textContent = name;

      const label = new CSS2DObject(div);
      label.position.set(0, 3, 0);
      rackGroup.add(label);

      // 添加边框高光
      const edgeGeometry = new THREE.EdgesGeometry(bodyGeometry);
      const edgeMaterial = new THREE.LineBasicMaterial({
        color: 0x64b5f6,
        linewidth: 1.5,
        transparent: true,
        opacity: 0.7
      });
      const edgeMesh = new THREE.LineSegments(edgeGeometry, edgeMaterial);
      rackGroup.add(edgeMesh);

      return rackGroup;
    },
    addRaycasterListener() {
      const container = this.$refs.container;
      container.addEventListener('click', this.onClick);
    },
    onClick(event) {
      const container = this.$refs.container;
      // 计算鼠标位置
      this.mouse.x = ((event.clientX - container.getBoundingClientRect().left) / container.clientWidth) * 2 - 1;
      this.mouse.y = -((event.clientY - container.getBoundingClientRect().top) / container.clientHeight) * 2 + 1;

      // 更新射线
      this.raycaster.setFromCamera(this.mouse, this.camera);

      // 检测相交对象
      const intersects = this.raycaster.intersectObjects(this.racks);

      if (intersects.length > 0) {
        // 查找包含userData的父级对象
      let selectedObject = intersects[0].object;
      while (selectedObject.parent && !selectedObject.userData.type) {
        selectedObject = selectedObject.parent;
      }

      if (selectedObject.userData.type === 'device') {
        const deviceId = selectedObject.userData.id;
        const device = this.sensors.find(s => s.id === deviceId);
        if (device) {
          this.selectedDevice = device;
          this.highlightDevice(selectedObject);
        }
      }
      } else {
        this.selectedDevice = null;
        this.racks.forEach(rack => this.resetDeviceHighlight(rack));
      }
    },
    highlightDevice(mesh) {
      // 重置所有设备高亮
      this.racks.forEach(rack => this.resetDeviceHighlight(rack));

      // 获取机身网格
      const body = mesh.children[0];
      body.material.color.set(0xff9800);
      body.material.emissive.set(0xffcc80);
      body.material.emissiveIntensity = 0.5;

      // 添加脉冲动画
      new TWEEN.Tween(mesh.scale)
        .to({ x: 1.05, y: 1.05, z: 1.05 }, 500)
        .easing(TWEEN.Easing.Sinusoidal.InOut)
        .yoyo(true)
        .repeat(Infinity)
        .start();

      // 添加光晕效果
      if (!mesh.glow) {
        const glowGeometry = new THREE.SphereGeometry(1.5, 32, 32);
        const glowMaterial = new THREE.MeshBasicMaterial({
          color: 0xff9800,
          transparent: true,
          opacity: 0.5,
          blending: THREE.AdditiveBlending
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.set(mesh.position.x, mesh.position.y + 2.5, mesh.position.z);
        this.scene.add(glow);
        mesh.glow = glow;
      }

      // 显示屏脉动效果
      const screen = mesh.children[2];
      new TWEEN.Tween(screen.material.emissiveIntensity)
        .to({ emissiveIntensity: 0.8 }, 500)
        .easing(TWEEN.Easing.Sinusoidal.InOut)
        .yoyo(true)
        .repeat(Infinity)
        .start();
    },
    resetDeviceHighlight(mesh) {
      // 重置机身颜色
      const body = mesh.children[0];
      body.material.color.set(0x2d2d44);
      body.material.emissive.set(0x000000);
      body.material.emissiveIntensity = 0;

      // 重置显示屏
      const screen = mesh.children[2];
      screen.material.emissiveIntensity = 0.3;

      // 移除光晕
      if (mesh.glow) {
        this.scene.remove(mesh.glow);
        mesh.glow.geometry.dispose();
        mesh.glow.material.dispose();
        mesh.glow = null;
      }

      TWEEN.removeAll();
      mesh.scale.set(1, 1, 1);
    },
    handleResize() {
      const container = this.$refs.container;
      const width = container.clientWidth || window.innerWidth;
      const height = container.clientHeight || window.innerHeight;

      const style = getComputedStyle(container);
      const paddingX = parseInt(style.paddingLeft) + parseInt(style.paddingRight);
      const paddingY = parseInt(style.paddingTop) + parseInt(style.paddingBottom);
      this.renderer.setSize(width - paddingX, height - paddingY);
      this.labelRenderer.setSize(width - paddingX, height - paddingY);

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    },
    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      TWEEN.update();
      if (this.renderer && this.labelRenderer) {
        this.renderer.render(this.scene, this.camera);
        this.labelRenderer.render(this.scene, this.camera);
      }
    },
    updateLastUpdateTime() {
      this.lastUpdate = this.formatTime(new Date());
    },
    formatTime(date) {
      return date.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    createSensor(x, z, y, color, id) {
      const div = document.createElement('div');
      div.className = 'sensor-panel';
      div.style.cssText = `
          background: rgba(255, 255, 255, 0.9);
          color: #333333;
          padding: 4px;
          border-radius: 4px;
          font-size: 14px;
          font-weight: normal;
          box-shadow: 0 2px 8px rgba(0,0,0,0.6);
          min-width: 100px;
          text-align: left;
          z-index: 1000;
        `;
      
      // 存储DOM元素引用以便更新
      const sensor = {
        id,
        element: div,
        position: { x: x + 1.75, y, z }
      };
      this.sensors.push(sensor);
      
      // 初始渲染
      this.updateSensorDisplay(sensor);
      
      const label = new CSS2DObject(div);
      label.position.set(sensor.position.x, sensor.position.y, sensor.position.z);
      this.scene.add(label);
    },
    updateSensorDisplay(sensor) {
      if (!sensor || !sensor.element) return;
      const data = this.sensorData[sensor.id] || { power: 0, reflection: 0 };
      const tempColor = data.power < 3 ? '#4caf50' : '#f44336';
      const humidityColor = data.reflection > 15 ? '#f44336' : '#4caf50';
      
      sensor.element.innerHTML = `
        <div><strong style="display: block; text-align: center;">${sensor.id}</strong></div>
        <div style="padding-left:5px;color: ${tempColor}">功率: ${data.power.toFixed(1)}KW</div>
        <div style="padding-left:5px;color: ${humidityColor}">反射: ${data.reflection.toFixed(1)}W</div>
      `;
    },
    startSensorDataUpdate() {
      // 模拟数据更新
      setInterval(() => {
        this.sensors.forEach(sensor => {
          if (this.sensorData[sensor.id]) {
            // 功率在5-7KW波动
            this.sensorData[sensor.id].power = Math.max(3, Math.min(5,
              this.sensorData[sensor.id].power + (Math.random() - 0.5) * 0.2
            ));
            // 反射在2-12W波动
            this.sensorData[sensor.id].reflection = Math.max(2, Math.min(12,
              this.sensorData[sensor.id].reflection + (Math.random() - 0.5) * 0.5
            ));
            this.updateSensorDisplay(sensor);
          }
        });
      }, 1000);
    }
  }
}
</script>

<style scoped>
.point-normal {
  background-color: #4caf50;
  box-shadow: 0 0 0.78125rem rgba(76, 175, 80, 0.7);
}
.point-warning {
  background-color: #ffc107;
  box-shadow: 0 0 0.78125rem rgba(255, 193, 7, 0.7);
}
.point-alarm {
  background-color: #f44336;
  box-shadow: 0 0 0.78125rem rgba(244, 67, 54, 0.7);
}
::v-deep .sensor-panel {
  background: rgba(255, 255, 255, 0.9);
  color: #333333;
  padding: 2px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: normal;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  min-width: 80px;
  text-align: left;
}
.device-details {
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  border-left: 3px solid #3b82f6;
}
::v-deep .rack-label {
  white-space: nowrap;
  font-weight: bold;
}
</style>